import { format, subDays } from 'date-fns';
import { resolveGoals } from './goalDefaults';
import { resolveMacroTargets } from './macroTargets';

const FASTING_PROTOCOL_HOURS = { '16:8': 16, '18:6': 18, '20:4': 20, '24:0': 24 };

export function buildDateLabels(days, endDate = new Date()) {
  return Array.from({ length: days }).map((_, index) => {
    const date = subDays(endDate, days - 1 - index);
    return {
      raw: format(date, 'yyyy-MM-dd'),
      label: format(date, 'MMM d'),
      weekday: format(date, 'EEE'),
    };
  });
}

export function sumCalories(metric) {
  return (metric?.food?.foodEntries || []).reduce((sum, entry) => sum + (entry.calories || 0), 0);
}

export function sumMacros(metric) {
  return (metric?.food?.foodEntries || []).reduce(
    (acc, entry) => ({
      protein: acc.protein + (entry.protein || 0),
      carbs: acc.carbs + (entry.carbs || 0),
      fats: acc.fats + (entry.fats || 0),
      fiber: acc.fiber + (entry.fiber || 0),
    }),
    { protein: 0, carbs: 0, fats: 0, fiber: 0 }
  );
}

export function computeFastingAnalytics(metricsRange) {
  const sessions = metricsRange
    .filter((m) => m.fasting?.completed || m.fasting?.isActive)
    .map((m) => {
      const protocol = m.fasting.protocol || '16:8';
      const plannedHours = FASTING_PROTOCOL_HOURS[protocol] || 16;
      const actualMinutes = m.fasting.actualFastingMinutes || (m.fasting.isActive ? plannedHours * 60 : plannedHours * 60);
      return {
        date: format(new Date(m.date), 'MMM d'),
        rawDate: m.date,
        protocol,
        completed: Boolean(m.fasting.completed),
        endedEarly: Boolean(m.fasting.endedEarly),
        hours: Number((actualMinutes / 60).toFixed(1)),
        adherence: Math.min(Math.round((actualMinutes / (plannedHours * 60)) * 100), 100),
      };
    });

  const completionRate = metricsRange.length
    ? Math.round((sessions.filter((s) => s.completed).length / metricsRange.length) * 100)
    : 0;
  const avgDuration = sessions.length
    ? (sessions.reduce((s, item) => s + item.hours, 0) / sessions.length).toFixed(1)
    : '0';

  const protocolBreakdown = Object.entries(
    sessions.reduce((acc, s) => {
      acc[s.protocol] = (acc[s.protocol] || 0) + 1;
      return acc;
    }, {})
  ).map(([name, count]) => ({ name, count }));

  const weeklyHeatmap = metricsRange.map((m) => ({
    day: format(new Date(m.date), 'EEE'),
    date: format(new Date(m.date), 'MMM d'),
    completed: Boolean(m.fasting?.completed),
    active: Boolean(m.fasting?.isActive),
    value: m.fasting?.completed ? 2 : m.fasting?.isActive ? 1 : 0,
  }));

  const adherenceTrend = sessions.map((s) => ({ date: s.date, adherence: s.adherence, hours: s.hours }));

  return { sessions, completionRate, avgDuration, protocolBreakdown, weeklyHeatmap, adherenceTrend };
}

export function computeNutritionAnalytics(metricsRange, calorieGoal, nutritionPreferences) {
  // No `|| 2000` fallback: a goal that was never set stays unset, and
  // resolveMacroTargets returns null macros rather than deriving them from an
  // invented calorie figure. See web/src/utils/macroTargets.js.
  const goal = typeof calorieGoal === 'number' && Number.isFinite(calorieGoal) && calorieGoal > 0 ? calorieGoal : null;
  const macroTargets = resolveMacroTargets({ calorieGoal: goal, nutritionPreferences });

  const daily = metricsRange.map((metric) => {
    const macros = sumMacros(metric);
    const calories = sumCalories(metric);
    const mealCounts = { breakfast: 0, lunch: 0, dinner: 0, snacks: 0 };
    (metric.food?.foodEntries || []).forEach((entry) => {
      if (mealCounts[entry.mealType] != null) mealCounts[entry.mealType] += entry.calories || 0;
    });
    return {
      date: format(new Date(metric.date), 'MMM d'),
      calories,
      protein: Math.round(macros.protein),
      carbs: Math.round(macros.carbs),
      fats: Math.round(macros.fats),
      fiber: Math.round(macros.fiber),
      ...mealCounts,
      calorieDelta: goal != null ? calories - goal : null,
    };
  });

  const avgCalories = daily.length ? Math.round(daily.reduce((s, d) => s + d.calories, 0) / daily.length) : 0;
  const avgMacros = daily.length
    ? {
        protein: Math.round(daily.reduce((s, d) => s + d.protein, 0) / daily.length),
        carbs: Math.round(daily.reduce((s, d) => s + d.carbs, 0) / daily.length),
        fats: Math.round(daily.reduce((s, d) => s + d.fats, 0) / daily.length),
      }
    : { protein: 0, carbs: 0, fats: 0 };

  const mealDistribution = [
    { meal: 'Breakfast', calories: daily.reduce((s, d) => s + d.breakfast, 0) },
    { meal: 'Lunch', calories: daily.reduce((s, d) => s + d.lunch, 0) },
    { meal: 'Dinner', calories: daily.reduce((s, d) => s + d.dinner, 0) },
    { meal: 'Snacks', calories: daily.reduce((s, d) => s + d.snacks, 0) },
  ];

  // Adherence against a target that was never set is not a number — it's
  // nothing to grade. Leave it null rather than measuring the user against a
  // fabricated baseline.
  // Both adherence figures divide by days with food actually logged, not
  // every day in the range -- an unlogged day is unmeasured, not a missed
  // target. (Protein used to divide by `daily.length`, so a user who logged
  // 3 of 30 days and hit their protein target every time read "10%".)
  const loggedDaysCount = Math.max(daily.filter((d) => d.calories > 0).length, 1);
  const adherence = {
    calories: goal != null
      ? Math.round((daily.filter((d) => d.calories > 0 && d.calories <= goal * 1.1).length / loggedDaysCount) * 100)
      : null,
    protein: macroTargets.protein != null
      ? Math.round((daily.filter((d) => d.calories > 0 && d.protein >= macroTargets.protein * 0.8).length / loggedDaysCount) * 100)
      : null,
  };

  return { daily, avgCalories, avgMacros, macroTargets, mealDistribution, adherence, goal };
}

export function computeActivityAnalytics(metricsRange, userData, currentHabits) {
  // Pass the current day's own habits record through when the caller has
  // one, the same way UserDashboardEnhanced.js resolves the ring it shows on
  // the dashboard -- otherwise this screen ignores a per-day goal saved via
  // "Log activity" and falls back straight to preferences, disagreeing with
  // the dashboard for any day whose stored goal differs from the user's
  // saved default.
  const { waterGoal, stepsGoal: stepGoal, exerciseGoal } = resolveGoals({
    habits: currentHabits,
    preferences: userData?.preferences,
  });

  const daily = metricsRange.map((metric) => {
    const habits = metric.habits || {};
    return {
      date: format(new Date(metric.date), 'MMM d'),
      weekday: format(new Date(metric.date), 'EEE'),
      steps: habits.steps || 0,
      water: habits.waterIntake || 0,
      exercise: Math.round((habits.exerciseHours || 0) * 60),
      stepsHit: (habits.steps || 0) >= stepGoal,
      waterHit: (habits.waterIntake || 0) >= waterGoal,
      exerciseHit: (habits.exerciseHours || 0) >= exerciseGoal,
    };
  });

  const weekdayPattern = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => {
    const matching = daily.filter((d) => d.weekday === day);
    return {
      day,
      steps: matching.length ? Math.round(matching.reduce((s, d) => s + d.steps, 0) / matching.length) : 0,
      water: matching.length ? Number((matching.reduce((s, d) => s + d.water, 0) / matching.length).toFixed(1)) : 0,
    };
  });

  const goalHitRate = {
    steps: Math.round((daily.filter((d) => d.stepsHit).length / Math.max(daily.length, 1)) * 100),
    water: Math.round((daily.filter((d) => d.waterHit).length / Math.max(daily.length, 1)) * 100),
    exercise: Math.round((daily.filter((d) => d.exerciseHit).length / Math.max(daily.length, 1)) * 100),
  };

  const totals = {
    steps: daily.reduce((s, d) => s + d.steps, 0),
    water: Number(daily.reduce((s, d) => s + d.water, 0).toFixed(1)),
    exercise: daily.reduce((s, d) => s + d.exercise, 0),
  };

  return { daily, weekdayPattern, goalHitRate, totals, stepGoal, waterGoal, exerciseGoal };
}

export function computeBiometricsAnalytics(healthRange, userData) {
  const series = (healthRange.length ? healthRange : []).map((entry) => ({
    date: format(new Date(entry.date), 'MMM d'),
    weight: entry.weight ?? userData?.weight ?? null,
    hrv: entry.hrv ?? null,
    restingHeartRate: entry.restingHeartRate ?? entry.heartRate ?? null,
    sleepMinutes: entry.sleepMinutes ?? null,
    activeCalories: entry.activeCaloriesBurned ?? null,
  }));

  const latest = series[series.length - 1] || {};
  const first = series.find((item) => item.weight != null) || {};
  const weightDelta = latest.weight != null && first.weight != null ? Number((latest.weight - first.weight).toFixed(1)) : null;

  const avgSleep = series.filter((s) => s.sleepMinutes).length
    ? Math.round(series.filter((s) => s.sleepMinutes).reduce((sum, s) => sum + s.sleepMinutes, 0) / series.filter((s) => s.sleepMinutes).length)
    : null;

  const avgHrv = series.filter((s) => s.hrv).length
    ? Math.round(series.filter((s) => s.hrv).reduce((sum, s) => sum + s.hrv, 0) / series.filter((s) => s.hrv).length)
    : null;

  return { series, latest, weightDelta, avgSleep, avgHrv };
}

export function computeInsightsAnalytics(metricsRange, healthRange, userData, nutritionPreferences) {
  const nutrition = computeNutritionAnalytics(metricsRange, userData?.dailyCalorieRequirement, nutritionPreferences);
  const activity = computeActivityAnalytics(metricsRange, userData);
  const fasting = computeFastingAnalytics(metricsRange);
  const biometrics = computeBiometricsAnalytics(healthRange, userData);

  const correlations = [];
  if (activity.daily.length && nutrition.daily.length) {
    const highStepDays = activity.daily.filter((d) => d.steps >= activity.stepGoal * 0.8).length;
    if (highStepDays >= 3) {
      correlations.push('Higher step counts tend to align with days you also hit hydration targets.');
    }
  }
  if (fasting.completionRate >= 60) {
    correlations.push(`Fasting consistency is strong at ${fasting.completionRate}% — keep your current protocol rhythm.`);
  } else if (fasting.completionRate > 0) {
    correlations.push(`Fasting completion is at ${fasting.completionRate}%. Tightening your eating window may improve metabolic momentum.`);
  }
  if (biometrics.weightDelta != null && biometrics.weightDelta < 0) {
    correlations.push(`Weight trend is down ${Math.abs(biometrics.weightDelta)} kg over the selected period.`);
  }
  if (nutrition.adherence.calories != null && nutrition.adherence.calories < 50) {
    correlations.push('Calorie logging is sparse — more consistent food entries will sharpen your nutrition insights.');
  }

  return {
    correlations,
    summary: [
      { label: 'Avg daily calories', value: nutrition.avgCalories, unit: 'kcal' },
      { label: 'Total steps', value: activity.totals.steps.toLocaleString(), unit: '' },
      { label: 'Fasting completion', value: `${fasting.completionRate}%`, unit: '' },
      { label: 'Avg sleep', value: biometrics.avgSleep ? `${Math.floor(biometrics.avgSleep / 60)}h ${biometrics.avgSleep % 60}m` : '—', unit: '' },
    ],
    nutrition,
    activity,
    fasting,
    biometrics,
  };
}

export function computeProtocolAnalytics(metricsRange) {
  const fasting = computeFastingAnalytics(metricsRange);
  const protocolStats = fasting.protocolBreakdown.map((item) => {
    const sessions = fasting.sessions.filter((s) => s.protocol === item.name);
    const completed = sessions.filter((s) => s.completed).length;
    const avgHours = sessions.length ? (sessions.reduce((s, x) => s + x.hours, 0) / sessions.length).toFixed(1) : '0';
    return {
      protocol: item.name,
      sessions: item.count,
      completed,
      successRate: sessions.length ? Math.round((completed / sessions.length) * 100) : 0,
      avgHours,
    };
  });

  return { protocolStats, fasting };
}
